import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import CachedImage from "components/CachedImage";
import { getAuthToken } from "lib/providers";
import { fetchWithMediaCache } from "lib/mediaCache";
import { downloadFile, filenameFromUrl } from "lib/shareFile";
import { useTranslation } from "locales";
import "./style.scss";

export interface GalleryImage {
  /** 720p thumbnail — shown instantly as a placeholder. */
  src: string;
  /** Full-resolution original — loaded for crisp zoom + download. */
  url: string;
  alt?: string;
  fileName: string;
  mediaType: string;
}

interface Props {
  images: GalleryImage[];
  startIndex?: number;
  onClose: () => void;
}

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const BUTTON_STEP = 1.5;
const WHEEL_STEP = 1.15;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const TAP_MOVE_TOLERANCE = 10;

const IDENTITY: Transform = { scale: 1, x: 0, y: 0 };

const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
const clampNum = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const centerX = () => window.innerWidth / 2;
const centerY = () => window.innerHeight / 2;

/**
 * Full-screen swipeable image gallery for a message's images.
 *
 * Beyond navigation (‹ › buttons, ←/→ keys, touch swipe, "i / N" counter) it
 * supports zoom — +/− buttons and wheel/double-click on desktop, pinch and
 * double-tap on mobile — plus drag-to-pan when zoomed and a download button.
 * The full-resolution original is fetched (auth-aware, like MediaPreviewModal)
 * and swapped in for the thumbnail so zoom stays crisp; a single <img> is the
 * sole intrinsic sizer, so the stage always has a real box even if the
 * thumbnail never resolves. ImageLightbox stays untouched for single-image
 * cases (inline GIF).
 */
function ImageGallery({ images, startIndex = 0, onClose }: Props) {
  const { t } = useTranslation();
  const count = images.length;
  const [index, setIndex] = useState(startIndex);

  const [tf, setTf] = useState<Transform>(IDENTITY);
  const [animated, setAnimated] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fullResUrl, setFullResUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  // Mirror the transform into a ref so the imperative gesture handlers read
  // the live value without re-subscribing.
  const tfRef = useRef(tf);
  tfRef.current = tf;

  const stageRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(true);
  // True between a drag's first move and the trailing synthesized click, so
  // handleBackdrop ignores the click that ends a pan over the dark backdrop.
  const pannedRef = useRef(false);
  // Removes the active drag's window listeners; also called on unmount.
  const dragCleanup = useRef<null | (() => void)>(null);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (dragCleanup.current) dragCleanup.current();
    },
    []
  );

  const current = images[index];

  const goPrev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);
  const goNext = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  // Reset zoom + transient download state whenever the visible image changes.
  useEffect(() => {
    setTf(IDENTITY);
    setAnimated(false);
    setDownloadError(false);
  }, [index]);

  // Load the full-resolution original (auth-aware: header + ?token on mobile
  // via fetchWithMediaCache). The thumbnail keeps showing until it resolves;
  // on failure we silently stay on the thumbnail.
  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    let created: string | null = null;
    setFullResUrl(null);
    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled) return;
        const url = await fetchWithMediaCache(current.url, token ? { Authorization: token } : {});
        if (cancelled) {
          if (url.startsWith("blob:")) URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setFullResUrl(url);
      } catch (e) {
        console.error("ImageGallery: full-res load failed:", e);
      }
    })();
    return () => {
      cancelled = true;
      if (created && created.startsWith("blob:")) {
        const old = created;
        // Defer one frame so the swapped-in <img src> has rendered, then free
        // the prior full-res blob (there is no cross-fade keeping it alive).
        requestAnimationFrame(() => URL.revokeObjectURL(old));
      }
    };
  }, [current && current.url]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Arrows are disabled while zoomed, matching the hidden ‹ › buttons and
      // the swipe-nav gate — otherwise they'd snap the zoom back to 1×.
      if (tfRef.current.scale !== 1) return;
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    },
    [onClose, goPrev, goNext]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  // Clamp the pan offset so the (scaled) image can't be dragged off-screen.
  const clampTransform = useCallback((next: Transform): Transform => {
    const el = stageRef.current;
    if (!el) return next;
    const maxX = Math.max(0, (el.offsetWidth * next.scale - window.innerWidth) / 2);
    const maxY = Math.max(0, (el.offsetHeight * next.scale - window.innerHeight) / 2);
    return { scale: next.scale, x: clampNum(next.x, -maxX, maxX), y: clampNum(next.y, -maxY, maxY) };
  }, []);

  // Re-clamp the offset when the viewport changes (rotation / window resize /
  // mobile URL-bar) so a previously-panned image doesn't sit out of bounds.
  useEffect(() => {
    const onResize = () => setTf((prev) => (prev.scale > 1 ? clampTransform(prev) : prev));
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [clampTransform]);

  // Zoom by `factor` keeping the screen point (clientX, clientY) fixed.
  const zoomAt = useCallback(
    (clientX: number, clientY: number, factor: number) => {
      setTf((prev) => {
        const newScale = clampScale(prev.scale * factor);
        if (newScale === prev.scale) return prev;
        const cx = clientX - centerX();
        const cy = clientY - centerY();
        const ratio = newScale / prev.scale;
        return clampTransform({
          scale: newScale,
          x: cx - (cx - prev.x) * ratio,
          y: cy - (cy - prev.y) * ratio,
        });
      });
    },
    [clampTransform]
  );

  const zoomButton = useCallback(
    (factor: number) => {
      setAnimated(true);
      zoomAt(centerX(), centerY(), factor);
    },
    [zoomAt]
  );

  const resetZoom = useCallback(() => {
    setAnimated(true);
    setTf(IDENTITY);
  }, []);

  // Wheel zoom — native non-passive listener so preventDefault is honored.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setAnimated(false);
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  // ---- Touch gestures: pinch-zoom, drag-pan, swipe-nav, double-tap ----
  const swipeStartX = useRef<number | null>(null);
  const panRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const pinchRef = useRef<{
    startDist: number;
    startScale: number;
    px: number; // anchored image-local point (x)
    py: number;
  } | null>(null);
  const tapRef = useRef<{ x: number; y: number; time: number; moved: boolean } | null>(null);
  const lastTapRef = useRef<{ x: number; y: number; time: number } | null>(null);

  const dist = (a: React.Touch, b: React.Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setAnimated(false);
    if (e.touches.length === 2) {
      const a = e.touches[0];
      const b = e.touches[1];
      const tfNow = tfRef.current;
      const midX = (a.clientX + b.clientX) / 2 - centerX();
      const midY = (a.clientY + b.clientY) / 2 - centerY();
      pinchRef.current = {
        startDist: dist(a, b),
        startScale: tfNow.scale,
        px: (midX - tfNow.x) / tfNow.scale,
        py: (midY - tfNow.y) / tfNow.scale,
      };
      swipeStartX.current = null;
      panRef.current = null;
      tapRef.current = null;
      // A multi-touch gesture must not let a stale pre-pinch tap complete a
      // spurious double-tap once a finger lifts.
      lastTapRef.current = null;
      return;
    }
    if (e.touches.length === 1) {
      const tch = e.touches[0];
      tapRef.current = { x: tch.clientX, y: tch.clientY, time: Date.now(), moved: false };
      if (tfRef.current.scale > 1) {
        panRef.current = { startX: tch.clientX, startY: tch.clientY, origX: tfRef.current.x, origY: tfRef.current.y };
      } else {
        swipeStartX.current = tch.clientX;
      }
    }
  }, []);

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (pinchRef.current && e.touches.length === 2) {
        const p = pinchRef.current;
        const a = e.touches[0];
        const b = e.touches[1];
        const newScale = clampScale(p.startScale * (dist(a, b) / p.startDist));
        const curMidX = (a.clientX + b.clientX) / 2 - centerX();
        const curMidY = (a.clientY + b.clientY) / 2 - centerY();
        setTf(clampTransform({ scale: newScale, x: curMidX - newScale * p.px, y: curMidY - newScale * p.py }));
        return;
      }
      if (panRef.current && e.touches.length === 1) {
        const p = panRef.current;
        const tch = e.touches[0];
        if (tapRef.current && Math.hypot(tch.clientX - tapRef.current.x, tch.clientY - tapRef.current.y) > TAP_MOVE_TOLERANCE) {
          tapRef.current.moved = true;
        }
        setTf((prev) => clampTransform({ scale: prev.scale, x: p.origX + (tch.clientX - p.startX), y: p.origY + (tch.clientY - p.startY) }));
        return;
      }
      if (e.touches.length === 1 && tapRef.current) {
        const tch = e.touches[0];
        if (Math.hypot(tch.clientX - tapRef.current.x, tch.clientY - tapRef.current.y) > TAP_MOVE_TOLERANCE) {
          tapRef.current.moved = true;
        }
      }
    },
    [clampTransform]
  );

  const handleDoubleTapToggle = useCallback(
    (clientX: number, clientY: number) => {
      setAnimated(true);
      if (tfRef.current.scale > 1) setTf(IDENTITY);
      else zoomAt(clientX, clientY, DOUBLE_TAP_SCALE);
    },
    [zoomAt]
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (pinchRef.current) {
        if (e.touches.length < 2) pinchRef.current = null;
        // A finger still down after the pinch becomes a fresh pan anchor.
        if (e.touches.length === 1 && tfRef.current.scale > 1) {
          const tch = e.touches[0];
          panRef.current = { startX: tch.clientX, startY: tch.clientY, origX: tfRef.current.x, origY: tfRef.current.y };
        }
        return;
      }

      const tap = tapRef.current;
      const wasPan = panRef.current !== null;
      panRef.current = null;

      // Double-tap to toggle zoom (a tap = pointer barely moved).
      if (tap && !tap.moved) {
        const now = Date.now();
        const last = lastTapRef.current;
        if (last && now - last.time < DOUBLE_TAP_MS && Math.hypot(tap.x - last.x, tap.y - last.y) < 30) {
          handleDoubleTapToggle(tap.x, tap.y);
          lastTapRef.current = null;
          tapRef.current = null;
          swipeStartX.current = null;
          return;
        }
        lastTapRef.current = { x: tap.x, y: tap.y, time: now };
      }
      tapRef.current = null;

      // Swipe to navigate — only when not zoomed and we weren't panning.
      if (!wasPan && swipeStartX.current !== null && tfRef.current.scale === 1) {
        const dx = e.changedTouches[0].clientX - swipeStartX.current;
        if (Math.abs(dx) > 50) {
          if (dx > 0) goPrev();
          else goNext();
        }
      }
      swipeStartX.current = null;
    },
    [goPrev, goNext, handleDoubleTapToggle]
  );

  // ---- Mouse drag-pan (desktop, only when zoomed) ----
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (tfRef.current.scale <= 1) return;
    e.preventDefault();
    setAnimated(false);
    setDragging(true);
    pannedRef.current = false;
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = tfRef.current.x;
    const origY = tfRef.current.y;
    const onMove = (ev: MouseEvent) => {
      pannedRef.current = true;
      const el = stageRef.current;
      const scale = tfRef.current.scale;
      const maxX = el ? Math.max(0, (el.offsetWidth * scale - window.innerWidth) / 2) : 0;
      const maxY = el ? Math.max(0, (el.offsetHeight * scale - window.innerHeight) / 2) : 0;
      setTf({ scale, x: clampNum(origX + (ev.clientX - startX), -maxX, maxX), y: clampNum(origY + (ev.clientY - startY), -maxY, maxY) });
    };
    const removeDrag = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      dragCleanup.current = null;
    };
    const onUp = () => {
      removeDrag();
      setDragging(false);
      // Clear the pan flag on the next tick — after any synthesized click has
      // reached handleBackdrop — so a later genuine backdrop click still closes.
      setTimeout(() => {
        pannedRef.current = false;
      }, 0);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    dragCleanup.current = removeDrag;
  }, []);

  const handleBackdrop = useCallback(
    (e: React.MouseEvent) => {
      // Swallow the click synthesized at the end of a drag-pan (mousedown on
      // the image, mouseup over the backdrop) so panning doesn't close.
      if (pannedRef.current) {
        pannedRef.current = false;
        return;
      }
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const handleDownload = useCallback(async () => {
    if (downloading || !current) return;
    setDownloading(true);
    setDownloadError(false);
    // Storage urlPart is extensionless, so fall back to a mediaType-derived
    // name — otherwise the save is an unnamed UUID (web) / empty File (share).
    const ext = (current.mediaType.split("/")[1] || "jpg").split("+")[0];
    const name = current.fileName || filenameFromUrl(current.url, `image.${ext}`);
    try {
      await downloadFile(fullResUrl ?? current.url, { filename: name, mimeType: current.mediaType, title: name });
    } catch (e) {
      console.error("ImageGallery: download failed:", e);
      if (mountedRef.current) {
        setDownloadError(true);
        setTimeout(() => {
          if (mountedRef.current) setDownloadError(false);
        }, 4000);
      }
    } finally {
      if (mountedRef.current) setDownloading(false);
    }
  }, [downloading, fullResUrl, current]);

  if (count === 0 || !current) return null;

  const zoomed = tf.scale > 1;
  const transformStyle: React.CSSProperties = {
    transform: `translate(${tf.x}px, ${tf.y}px) scale(${tf.scale})`,
    transition: animated ? "transform 180ms ease" : "none",
    cursor: dragging ? "grabbing" : zoomed ? "grab" : "zoom-in",
  };

  return ReactDOM.createPortal(
    <div className="image-gallery" onClick={handleBackdrop}>
      <button className="image-gallery__close" onClick={onClose} type="button" aria-label={t("Close")}>
        &times;
      </button>

      {count > 1 && !zoomed && (
        <button
          className="image-gallery__nav __prev"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          type="button"
          aria-label="Previous"
        >
          &#8249;
        </button>
      )}

      <div
        className="image-gallery__stage"
        ref={stageRef}
        style={transformStyle}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onDoubleClick={(e) => handleDoubleTapToggle(e.clientX, e.clientY)}
      >
        {/* Single intrinsic sizer: the thumbnail (auth/cache-aware) until the
            full-res blob is ready, then the full-res image itself. */}
        {fullResUrl ? (
          <img className="image-gallery__image" src={fullResUrl} alt={current.alt || ""} draggable={false} />
        ) : (
          <CachedImage className="image-gallery__image" src={current.src} alt={current.alt || ""} draggable={false} />
        )}
      </div>

      {count > 1 && !zoomed && (
        <button
          className="image-gallery__nav __next"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          type="button"
          aria-label="Next"
        >
          &#8250;
        </button>
      )}

      <div className="image-gallery__controls" onClick={(e) => e.stopPropagation()}>
        <button
          className="image-gallery__ctrl"
          onClick={() => zoomButton(1 / BUTTON_STEP)}
          type="button"
          disabled={tf.scale <= MIN_SCALE}
          title={t("Zoom out")}
          aria-label={t("Zoom out")}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M5 11h14v2H5z" fill="currentColor" />
          </svg>
        </button>

        <span className="image-gallery__zoom-level">{Math.round(tf.scale * 100)}%</span>

        <button
          className="image-gallery__ctrl"
          onClick={() => zoomButton(BUTTON_STEP)}
          type="button"
          disabled={tf.scale >= MAX_SCALE}
          title={t("Zoom in")}
          aria-label={t("Zoom in")}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z" fill="currentColor" />
          </svg>
        </button>

        {zoomed && (
          <button
            className="image-gallery__ctrl"
            onClick={resetZoom}
            type="button"
            title={t("Reset zoom")}
            aria-label={t("Reset zoom")}
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}

        <button
          className={"image-gallery__ctrl" + (downloadError ? " __error" : "")}
          onClick={handleDownload}
          type="button"
          disabled={downloading}
          title={downloadError ? t("Download failed") : t("Download")}
          aria-label={downloadError ? t("Download failed") : t("Download")}
          aria-busy={downloading}
        >
          {downloading ? (
            <span className="image-gallery__spinner" />
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor" />
            </svg>
          )}
        </button>
      </div>

      <div className="image-gallery__footer">
        {count > 1 && (
          <span className="image-gallery__counter">
            {index + 1} / {count}
          </span>
        )}
        {current.fileName && <span className="image-gallery__filename">{current.fileName}</span>}
      </div>
    </div>,
    document.body
  );
}

export default ImageGallery;
